/**
 * Per-employee task tracker (manager+).
 *
 * "What is this person actually working on right now?" — distinct from
 * Reports' analytics view. Sections:
 *
 *   1. Open follow-ups assigned to them, overdue called out at top.
 *   2. Open deals assigned to them.
 *   3. Contacts they own (assigned_to_id == userId).
 *   4. Recently completed follow-ups (last 30 days, capped).
 *
 * All data is fetched from existing list endpoints with the appropriate
 * filter — no new aggregate endpoint. Each section caps at a sensible
 * row count and links out to the corresponding global list page filtered
 * to this rep, so a manager who wants the full list always has a way
 * out.
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Briefcase,
  CalendarClock,
  CheckCircle2,
  Loader2,
  Users,
} from "lucide-react";
import { useMemo } from "react";

import { BackLink } from "../components/BackLink";
import { type Column, DataTable } from "../components/DataTable";
import {
  type Contact,
  type Deal,
  type FollowUp,
  type Role,
  contactsApi,
  dealsApi,
  followupsApi,
  usersApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/team/$userId")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin" && user.role !== "manager") {
      throw redirect({ to: "/deals" });
    }
  },
  component: TeamDetailPage,
});

const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  manager: "Manager",
  production: "Production",
  sales: "Sales",
};

const ROLE_BADGE: Record<Role, string> = {
  admin: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  production: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  manager: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  sales: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
};

const STAGE_LABEL: Record<string, string> = {
  new: "New",
  contacted: "Contacted",
  sample_shared: "Sample shared",
  negotiation: "Negotiation",
  won: "Won",
  lost: "Lost",
};

const STATUS_BADGE: Record<string, string> = {
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  follow_up: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  cold: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300",
};

const FOLLOWUPS_PAGE_LIMIT = 100;
const DEALS_PAGE_LIMIT = 50;
const CONTACTS_PAGE_LIMIT = 50;

function TeamDetailPage() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();

  // We resolve the target user from the existing /api/users list rather
  // than introducing a new "get one" endpoint — the list is already
  // cached by the index page and is small enough that this is fine.
  const usersQuery = useQuery({
    queryKey: ["team:users-all"],
    queryFn: () => usersApi.list({ active_only: false }),
  });
  const targetUser = useMemo(
    () => (usersQuery.data?.items ?? []).find((u) => u.id === userId) ?? null,
    [usersQuery.data, userId],
  );

  const openFollowupsQ = useQuery({
    queryKey: ["team:followups-open", userId],
    queryFn: () =>
      followupsApi.list({
        assignee_id: userId,
        open_only: true,
        limit: FOLLOWUPS_PAGE_LIMIT,
      }),
  });

  const recentDoneFollowupsQ = useQuery({
    queryKey: ["team:followups-done", userId],
    queryFn: () =>
      followupsApi.list({
        assignee_id: userId,
        open_only: false,
        limit: FOLLOWUPS_PAGE_LIMIT,
      }),
  });

  const dealsQ = useQuery({
    queryKey: ["team:deals", userId],
    queryFn: () =>
      dealsApi.list({
        assigned_to_id: userId,
        open_only: true,
        limit: DEALS_PAGE_LIMIT,
      }),
  });

  const contactsQ = useQuery({
    queryKey: ["team:contacts", userId],
    queryFn: () =>
      contactsApi.list({
        assigned_to_id: userId,
        limit: CONTACTS_PAGE_LIMIT,
      }),
  });

  // Split open follow-ups into overdue (red) and upcoming (default).
  // Server returns scheduled-asc, so the first chunk is the overdue
  // tail.
  const openFollowups = openFollowupsQ.data?.items ?? [];
  const overdueFollowups = openFollowups.filter((f) => f.is_overdue);
  const upcomingFollowups = openFollowups.filter((f) => !f.is_overdue);

  // "Recently completed" = aggregate-completed in the last 30 days.
  const recentDoneCutoff = useMemo(() => Date.now() - 30 * 86400_000, []);
  const recentDone = (recentDoneFollowupsQ.data?.items ?? [])
    .filter((f) => f.is_completed && f.completed_at)
    .filter((f) => new Date(f.completed_at as string).getTime() >= recentDoneCutoff)
    .sort((a, b) => {
      const ta = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const tb = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return tb - ta;
    })
    .slice(0, 25);

  const isAnyLoading =
    usersQuery.isLoading ||
    openFollowupsQ.isLoading ||
    recentDoneFollowupsQ.isLoading ||
    dealsQ.isLoading ||
    contactsQ.isLoading;

  return (
    <div className="page-shell space-y-6 p-4 sm:p-6">
      <div>
        <BackLink fallbackTo="/team" fallbackLabel="team" size="xs" />
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Users className="size-5 opacity-70" aria-hidden />
            {targetUser?.name || targetUser?.email || "Teammate"}
          </h1>
          <p className="flex flex-wrap items-center gap-2 text-sm opacity-60">
            <span className="truncate">{targetUser?.email}</span>
            {targetUser && (
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${ROLE_BADGE[targetUser.role]}`}
              >
                {ROLE_LABEL[targetUser.role]}
              </span>
            )}
            {targetUser && !targetUser.is_active && (
              <span className="inline-block rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
                Disabled
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAnyLoading && (
            <span className="inline-flex items-center gap-1.5 text-xs opacity-60">
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
              Loading…
            </span>
          )}
        </div>
      </header>

      {/* Section: overdue follow-ups (red call-out, only when present). */}
      {overdueFollowups.length > 0 && (
        <FollowupSection
          title={`Overdue (${overdueFollowups.length})`}
          subtitle="Past their scheduled time and still open. These are blocking work."
          icon={<AlertTriangle className="size-4 text-red-600 dark:text-red-400" aria-hidden />}
          rows={overdueFollowups}
          tone="overdue"
        />
      )}

      {/* Section: upcoming open follow-ups. */}
      <FollowupSection
        title={`Open follow-ups${upcomingFollowups.length ? ` (${upcomingFollowups.length})` : ""}`}
        subtitle="Future follow-ups assigned to this rep."
        icon={<CalendarClock className="size-4 opacity-70" aria-hidden />}
        rows={upcomingFollowups}
        tone="open"
      />

      {/* Section: open deals. */}
      <DealSection
        rows={dealsQ.data?.items ?? []}
        seeAllHref={() => navigate({ to: "/deals", search: { assigned_to: userId } as never })}
      />

      {/* Section: contacts owned. */}
      <ContactSection rows={contactsQ.data?.items ?? []} />

      {/* Section: completed follow-ups, last 30 days. */}
      <FollowupSection
        title={`Completed in the last 30 days${recentDone.length ? ` (${recentDone.length})` : ""}`}
        subtitle="Follow-ups this rep has finished off recently."
        icon={
          <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
        }
        rows={recentDone}
        tone="done"
      />
    </div>
  );
}

// ── Follow-up table section ─────────────────────────────────────────────────

function FollowupSection({
  title,
  subtitle,
  icon,
  rows,
  tone,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  rows: FollowUp[];
  tone: "overdue" | "open" | "done";
}) {
  const columns: Column<FollowUp>[] = [
    {
      header: tone === "done" ? "What" : "Follow-up on",
      cell: (f) => {
        const summary = f.parent_summary;
        return (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              {summary && <ParentKindChip summary={summary} viaDeal={f.parent_type === "deal"} />}
              <span className="truncate text-sm font-medium">
                {summary?.name ?? <span className="opacity-50">(parent unavailable)</span>}
              </span>
            </div>
            {/*
              For overdue / open the note is the operative detail —
              what the rep is meant to do. For "done" we deliberately
              don't show it because the section is "did this person
              actually finish work?" — the parent name + when are the
              read.
            */}
            {tone !== "done" && (
              <div className="truncate text-xs opacity-60">
                {f.note || <span className="italic opacity-70">(no note)</span>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: tone === "done" ? "Completed" : "Scheduled",
      width: "w-44",
      align: "right",
      cell: (f) => {
        const at = tone === "done" ? f.completed_at : f.scheduled_at;
        return (
          <span
            className={`text-xs ${
              tone === "overdue" ? "font-medium text-red-700 dark:text-red-300" : "opacity-70"
            }`}
            title={at ? new Date(at).toLocaleString() : undefined}
          >
            {at ? formatRelative(at) : "—"}
          </span>
        );
      },
    },
  ];

  return (
    <section className="space-y-2">
      <header className="flex items-baseline gap-2">
        {icon}
        <h2 className="text-sm font-semibold">{title}</h2>
        <span className="text-xs opacity-60">— {subtitle}</span>
      </header>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(f) => f.id}
        rowHref={(f) =>
          // Click destination follows the polymorphic parent — that's
          // the entity the work is actually being done against. For
          // deal-parented follow-ups we want the deal page (the rep is
          // working a deal); for contact / account follow-ups we go
          // direct to that record. Standalone follow-ups have no
          // parent — return null so the row is non-clickable until
          // the followup-detail surface lands.
          //
          // The parent_summary.name is used for the row's *display*
          // label (so the manager sees a real name, not a raw deal id),
          // but the summary's href is intentionally NOT used here — it
          // points at the deal's parent contact/account, which is one
          // hop away from where the rep's actual work is.
          f.parent_type === "none" || f.parent_id === null
            ? null
            : `/${f.parent_type}s/${f.parent_id}`
        }
        empty={
          <div className="rounded-xl border border-dashed border-[var(--border)] p-6 text-center text-xs opacity-60">
            None.
          </div>
        }
      />
    </section>
  );
}

/**
 * Tiny chip that shows what kind of entity the row links to. Always
 * reflects the *resolved* href target (contact or account) — if the
 * follow-up was on a deal, we add a faint "via deal" hint so the
 * manager knows there's a deal in the chain.
 */
function ParentKindChip({
  summary,
  viaDeal,
}: {
  summary: { href_kind: "contact" | "account" };
  viaDeal: boolean;
}) {
  const isAccount = summary.href_kind === "account";
  const label = isAccount ? "ACCT" : "CT";
  const klass = isAccount
    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
    : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200";
  return (
    <span className="flex shrink-0 items-center gap-1">
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${klass}`}
        title={isAccount ? "Account" : "Contact"}
      >
        {label}
      </span>
      {viaDeal && (
        <span
          className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
          title="The follow-up is on a deal under this contact/account"
        >
          via deal
        </span>
      )}
    </span>
  );
}

// ── Deals section ───────────────────────────────────────────────────────────

function DealSection({
  rows,
  seeAllHref,
}: {
  rows: Deal[];
  seeAllHref: () => void;
}) {
  const columns: Column<Deal>[] = [
    {
      header: "Deal with",
      cell: (d) => {
        // Two display modes:
        //   1. Contact-led: contact name primary, account chip on the
        //      side, phone on the subtitle. The common v1.2 case.
        //   2. Account-only: account name primary, no subtitle.
        //
        // Falls through to a faint placeholder if both summaries are
        // missing (parent soft-deleted) so we never print a raw uuid.
        const c = d.contact_summary;
        const a = d.account_summary;
        if (c) {
          return (
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-sm font-medium">{c.name}</span>
                {a && (
                  <span
                    className="shrink-0 truncate rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                    title={`Account: ${a.name}`}
                  >
                    {a.name}
                  </span>
                )}
              </div>
              {c.phone && <div className="truncate text-xs opacity-60">{c.phone}</div>}
            </div>
          );
        }
        if (a) {
          return (
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <span
                  className="shrink-0 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                  title="Account-only deal"
                >
                  ACCT
                </span>
                <span className="truncate text-sm font-medium">{a.name}</span>
              </div>
            </div>
          );
        }
        return <span className="text-sm opacity-50">(parent unavailable)</span>;
      },
    },
    {
      header: "Stage",
      width: "w-32",
      cell: (d) => (
        <span className="text-xs uppercase tracking-wide opacity-80">
          {STAGE_LABEL[d.stage] ?? d.stage}
        </span>
      ),
    },
    {
      header: "Status",
      width: "w-28",
      cell: (d) => (
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${STATUS_BADGE[d.status] ?? ""}`}
        >
          {d.status.replace("_", " ")}
        </span>
      ),
    },
  ];

  return (
    <section className="space-y-2">
      <header className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <Briefcase className="size-4 opacity-70" aria-hidden />
          <h2 className="text-sm font-semibold">
            Open deals{rows.length ? ` (${rows.length})` : ""}
          </h2>
          <span className="text-xs opacity-60">— Assigned to this rep, not yet closed.</span>
        </div>
        <button type="button" onClick={seeAllHref} className="text-xs opacity-70 hover:opacity-100">
          See all in /deals →
        </button>
      </header>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(d) => d.id}
        rowHref={(d) => `/deals/${d.id}`}
        empty={
          <div className="rounded-xl border border-dashed border-[var(--border)] p-6 text-center text-xs opacity-60">
            None.
          </div>
        }
      />
    </section>
  );
}

// ── Contacts section ────────────────────────────────────────────────────────

function ContactSection({ rows }: { rows: Contact[] }) {
  const columns: Column<Contact>[] = [
    {
      header: "Name",
      cell: (c) => (
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{c.name}</div>
          <div className="truncate text-xs opacity-60">{c.phone}</div>
        </div>
      ),
    },
    {
      header: "Email",
      cell: (c) => <span className="text-xs opacity-70">{c.email || "—"}</span>,
      hideOnSmall: true,
    },
    {
      header: "Created",
      width: "w-36",
      align: "right",
      cell: (c) => (
        <span className="text-xs opacity-70" title={c.created_at}>
          {formatRelative(c.created_at)}
        </span>
      ),
    },
  ];

  return (
    <section className="space-y-2">
      <header className="flex items-baseline gap-2">
        <Users className="size-4 opacity-70" aria-hidden />
        <h2 className="text-sm font-semibold">
          Contacts owned{rows.length ? ` (${rows.length})` : ""}
        </h2>
        <span className="text-xs opacity-60">— assigned_to is this rep.</span>
      </header>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        rowHref={(c) => `/contacts/${c.id}`}
        empty={
          <div className="rounded-xl border border-dashed border-[var(--border)] p-6 text-center text-xs opacity-60">
            None.
          </div>
        }
      />
    </section>
  );
}

// ── Date helper (shared with reports drill-down) ────────────────────────────

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  const past = diffSec >= 0;
  const abs = Math.abs(diffSec);
  if (abs < 60) return past ? `${abs}s ago` : `in ${abs}s`;
  if (abs < 3600) return past ? `${Math.round(abs / 60)}m ago` : `in ${Math.round(abs / 60)}m`;
  if (abs < 86400) return past ? `${Math.round(abs / 3600)}h ago` : `in ${Math.round(abs / 3600)}h`;
  return past ? `${Math.round(abs / 86400)}d ago` : `in ${Math.round(abs / 86400)}d`;
}
